import { useEffect, useRef, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  QRCode,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Edit2,
  FileText,
  Package,
  Plus,
  QrCode,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import axiosClient from "../../api/axiosClient";
import "./AdminProductsPage.css";

const { Text } = Typography;

type ProductCategory = "Hair Care" | "Styling" | "Grooming" | "Skin Care" | "Color";

type Product = {
  id: string;
  name: string;
  category: ProductCategory;
  brand: string;
  sku: string;
  cost: number;
  price: number;
  stock: number;
  lowStockThreshold: number;
};

const CATEGORIES: ProductCategory[] = ["Hair Care", "Styling", "Grooming", "Skin Care", "Color"];

const CATEGORY_COLORS: Record<ProductCategory, string> = {
  "Hair Care": "blue",
  Styling: "purple",
  Grooming: "green",
  "Skin Care": "magenta",
  Color: "orange",
};

function calcMargin(cost: number, price: number): string {
  if (!cost || !price || price === 0) return "—";
  return `${(((price - cost) / price) * 100).toFixed(0)}%`;
}

function generateSku(name: string, category: ProductCategory, brand: string): string {
  const catMap: Record<ProductCategory, string> = {
    "Hair Care": "HC",
    Styling: "ST",
    Grooming: "GR",
    "Skin Care": "SK",
    Color: "CL",
  };
  const cat = catMap[category] ?? "XX";
  const brd = (brand || "XX").slice(0, 2).toUpperCase();
  const nam = (name || "XX").replace(/\s+/g, "").slice(0, 3).toUpperCase();
  const num = String(Math.floor(Math.random() * 900) + 100);
  return `${cat}-${brd}${nam}-${num}`;
}

function fromAPI(p: Record<string, unknown>): Product {
  return {
    id: p.product_id as string,
    name: p.name as string,
    category: p.category as ProductCategory,
    brand: p.brand as string,
    sku: p.sku as string,
    cost: p.cost as number,
    price: p.price as number,
    stock: p.stock as number,
    lowStockThreshold: p.low_stock_threshold as number,
  };
}

function toAPI(p: Omit<Product, "id">) {
  return {
    name: p.name,
    category: p.category,
    brand: p.brand,
    sku: p.sku,
    cost: p.cost,
    price: p.price,
    stock: p.stock,
    low_stock_threshold: p.lowStockThreshold,
  };
}

// ── CSV Import ────────────────────────────────────────────────────────────────

type ImportStatus = "idle" | "parsing" | "preview" | "done" | "error";

type ImportResult = {
  added: Omit<Product, "id">[];
  skipped: { row: number; reason: string }[];
};

function parseCSV(text: string, existingSkus: Set<string>): ImportResult {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) {
    return { added: [], skipped: [{ row: 0, reason: "File appears empty or has no data rows" }] };
  }
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ""));
  const col = (names: string[]) =>
    names.reduce((best, n) => {
      const i = headers.findIndex((h) => h.includes(n));
      return i >= 0 ? i : best;
    }, -1);

  const nameIdx = col(["name", "product", "productname", "title"]);
  const catIdx = col(["category", "cat", "type"]);
  const brandIdx = col(["brand", "manufacturer", "make"]);
  const skuIdx = col(["sku", "code", "barcode", "upc", "id"]);
  const costIdx = col(["cost", "costprice", "wholesale", "purchase"]);
  const priceIdx = col(["price", "retail", "saleprice", "msrp"]);
  const stockIdx = col(["stock", "qty", "quantity", "inventory", "onhand"]);
  const threshIdx = col(["threshold", "lowstock", "reorder", "minimum", "min"]);

  const added: Omit<Product, "id">[] = [];
  const skipped: { row: number; reason: string }[] = [];

  for (let i = 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    const cols: string[] = [];
    let cur = "", inQ = false;
    for (const ch of raw + ",") {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === "," && !inQ) { cols.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    const g = (idx: number) => (idx >= 0 ? cols[idx]?.trim() ?? "" : "");
    const name = g(nameIdx);
    if (!name) { skipped.push({ row: i + 1, reason: "Missing product name" }); continue; }
    const rawSku = g(skuIdx) || generateSku(name, "Hair Care", g(brandIdx));
    if (existingSkus.has(rawSku)) {
      skipped.push({ row: i + 1, reason: `SKU "${rawSku}" already exists` });
      continue;
    }
    const cost = parseFloat(g(costIdx).replace(/[$,]/g, "")) || 0;
    const price = parseFloat(g(priceIdx).replace(/[$,]/g, "")) || cost * 1.5 || 1;
    const stock = parseInt(g(stockIdx).replace(/[^0-9]/g, "")) || 0;
    const threshold = parseInt(g(threshIdx).replace(/[^0-9]/g, "")) || 10;
    const rawCat = g(catIdx).toLowerCase();
    const category: ProductCategory =
      rawCat.includes("styling") ? "Styling"
      : rawCat.includes("groom") ? "Grooming"
      : rawCat.includes("skin") ? "Skin Care"
      : rawCat.includes("color") || rawCat.includes("colour") ? "Color"
      : "Hair Care";

    added.push({ name, category, brand: g(brandIdx) || "Unknown", sku: rawSku, cost, price, stock, lowStockThreshold: threshold });
    existingSkus.add(rawSku);
  }
  return { added, skipped };
}

function ImportModal({
  onClose,
  onImport,
  existingSkus,
}: {
  onClose: () => void;
  onImport: (products: Omit<Product, "id">[]) => void;
  existingSkus: Set<string>;
}) {
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function processFile(file: File) {
    setFileName(file.name);
    setStatus("parsing");
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["csv", "tsv", "txt"].includes(ext)) {
      const formatMessages: Record<string, string> = {
        pdf: "PDF import coming soon. Please export your product list as CSV instead.",
        xlsx: "Excel files aren't supported yet. In Excel: File → Save As → CSV, then import that file.",
        xls: "Excel files aren't supported yet. In Excel: File → Save As → CSV, then import that file.",
        ods: "OpenDocument spreadsheets aren't supported yet. Export as CSV instead.",
      };
      const reason = (ext && formatMessages[ext]) || `Format .${ext} not supported. Please export as CSV.`;
      setTimeout(() => {
        setStatus("error");
        setResult({ added: [], skipped: [{ row: 0, reason }] });
      }, 600);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const skusCopy = new Set(existingSkus);
      const parsed = parseCSV(text, skusCopy);
      setResult(parsed);
      setStatus(parsed.added.length > 0 ? "preview" : "error");
    };
    reader.onerror = () => setStatus("error");
    reader.readAsText(file);
  }

  return (
    <Modal
      open
      title="Import Products"
      onCancel={onClose}
      footer={
        status === "preview" && result ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              type="link"
              onClick={() => { setStatus("idle"); setResult(null); setFileName(""); if (fileRef.current) fileRef.current.value = ""; }}
            >
              ← Choose different file
            </Button>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => { onImport(result.added); setStatus("done"); }}
              >
                Import {result.added.length} Products
              </Button>
            </Space>
          </div>
        ) : status === "done" ? (
          <Button type="primary" onClick={onClose}>Done</Button>
        ) : null
      }
      width={560}
    >
      {status === "done" && (
        <div className="import-done">
          <CheckCircle2 size={44} className="import-done-icon" />
          <div className="import-done-title">Import Complete</div>
          <div className="import-done-sub">{result?.added.length} products added to inventory.</div>
        </div>
      )}

      {(status === "idle" || status === "parsing") && (
        <>
          <div
            className={`import-dropzone${dragOver ? " import-dropzone-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls,.ods,.pdf"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }}
              style={{ display: "none" }}
            />
            {status === "parsing" ? (
              <div className="import-parsing">
                <div className="import-spinner" />
                <span>Parsing {fileName}…</span>
              </div>
            ) : (
              <>
                <Upload size={36} className="import-drop-icon" />
                <div className="import-drop-title">Drop CSV here or click to browse</div>
                <div className="import-drop-sub">Supports CSV, TSV, TXT</div>
              </>
            )}
          </div>
          <div className="import-hint">
            <FileText size={13} />
            <div>
              <div className="import-hint-label">Expected columns (flexible)</div>
              <code>name, category, brand, sku, cost, price, stock, threshold</code>
            </div>
          </div>
        </>
      )}

      {status === "preview" && result && (
        <>
          <div className="import-preview-banner import-banner-success">
            <CheckCircle2 size={16} />
            <span><strong>{result.added.length} products</strong> ready to import{result.skipped.length > 0 ? ` · ${result.skipped.length} rows skipped` : ""}</span>
          </div>
          <div className="import-table-wrap">
            <table className="import-table">
              <thead>
                <tr>
                  <th>Product</th><th>Brand</th><th>SKU</th>
                  <th className="text-right">Price</th><th className="text-right">Stock</th>
                </tr>
              </thead>
              <tbody>
                {result.added.slice(0, 50).map((p, i) => (
                  <tr key={i}>
                    <td>{p.name}</td><td>{p.brand}</td>
                    <td className="mono">{p.sku}</td>
                    <td className="text-right">${p.price.toFixed(2)}</td>
                    <td className="text-right">{p.stock}</td>
                  </tr>
                ))}
                {result.added.length > 50 && (
                  <tr><td colSpan={5} className="more-row">…and {result.added.length - 50} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result.skipped.length > 0 && (
            <div className="import-preview-banner import-banner-warn">
              <AlertTriangle size={14} />
              <div>
                <strong>{result.skipped.length} rows skipped</strong>
                <ul>
                  {result.skipped.slice(0, 5).map((s, i) => (
                    <li key={i}>{s.row > 0 ? `Row ${s.row}: ` : ""}{s.reason}</li>
                  ))}
                  {result.skipped.length > 5 && <li>…and {result.skipped.length - 5} more</li>}
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {status === "error" && result && (
        <>
          <div className="import-preview-banner import-banner-error">
            <AlertTriangle size={16} />
            <div>
              <strong>Could not import file</strong>
              {result.skipped.map((s, i) => <div key={i}>{s.reason}</div>)}
            </div>
          </div>
          <Button
            block
            onClick={() => { setStatus("idle"); setResult(null); setFileName(""); if (fileRef.current) fileRef.current.value = ""; }}
          >
            Try a different file
          </Button>
        </>
      )}
    </Modal>
  );
}

// ── Product Form Modal ────────────────────────────────────────────────────────

function ProductModal({
  product,
  onClose,
  onSave,
}: {
  product?: Product;
  onClose: () => void;
  onSave: (p: Omit<Product, "id">) => void;
}) {
  const [form] = Form.useForm();
  const [category, setCategory] = useState<ProductCategory>(product?.category ?? "Hair Care");

  const cost = Form.useWatch("cost", form) ?? 0;
  const price = Form.useWatch("price", form) ?? 0;

  function handleAutoSku() {
    const vals = form.getFieldsValue();
    form.setFieldValue("sku", generateSku(vals.name ?? "", category, vals.brand ?? ""));
  }

  async function handleOk() {
    const vals = await form.validateFields();
    onSave({
      name: vals.name.trim(),
      category: vals.category,
      brand: vals.brand.trim(),
      sku: vals.sku.trim(),
      cost: vals.cost ?? 0,
      price: vals.price,
      stock: vals.stock ?? 0,
      lowStockThreshold: vals.lowStockThreshold ?? 10,
    });
  }

  return (
    <Modal
      open
      title={product ? "Edit Product" : "Add New Product"}
      onCancel={onClose}
      onOk={handleOk}
      okText={product ? "Save Changes" : "Add Product"}
      width={520}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          product
            ? { ...product }
            : { category: "Hair Care", lowStockThreshold: 10 }
        }
        style={{ marginTop: 16 }}
      >
        <Form.Item name="name" label="Product Name" rules={[{ required: true, message: "Required" }]}>
          <Input placeholder="e.g. Moisturizing Shampoo" />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select
              onChange={(v) => setCategory(v)}
              options={CATEGORIES.map((c) => ({ label: c, value: c }))}
            />
          </Form.Item>
          <Form.Item name="brand" label="Brand" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Olaplex" />
          </Form.Item>
        </div>

        <Form.Item name="sku" label="SKU" rules={[{ required: true, message: "Required" }]}>
          <Input
            placeholder="e.g. HC-OLA-001"
            style={{ fontFamily: "monospace" }}
            addonAfter={
              <span style={{ cursor: "pointer", fontSize: 12, color: "#1677ff" }} onClick={handleAutoSku}>
                Auto-generate
              </span>
            }
          />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Form.Item name="cost" label="Cost ($)" rules={[{ required: true, message: "Required" }]}>
            <InputNumber style={{ width: "100%" }} min={0} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item name="price" label="Retail Price ($)" rules={[{ required: true, message: "Required" }, { type: "number", min: 0.01, message: "Must be > 0" }]}>
            <InputNumber style={{ width: "100%" }} min={0.01} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item label="Margin">
            <div className="margin-display">{calcMargin(cost, price)}</div>
          </Form.Item>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="stock" label="Current Stock" rules={[{ required: true, message: "Required" }]}>
            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
          </Form.Item>
          <Form.Item name="lowStockThreshold" label="Low Stock Alert" rules={[{ required: true, message: "Required" }, { type: "number", min: 0, message: "Must be ≥ 0" }]}>
            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

// ── Delete Confirm ────────────────────────────────────────────────────────────

function DeleteModal({ product, onClose, onConfirm }: { product: Product; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      open
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={16} color="#dc2626" />
          </span>
          Delete Product
        </span>
      }
      onCancel={onClose}
      onOk={onConfirm}
      okText="Delete"
      okButtonProps={{ danger: true }}
      width={400}
    >
      <p style={{ margin: "12px 0" }}>
        Are you sure you want to delete <strong>{product.name}</strong>? This will permanently remove it from inventory.
      </p>
    </Modal>
  );
}

// ── QR Code ──────────────────────────────────────────────────────────────────

function QRModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const wrapRef = useRef<HTMLDivElement>(null);

  function handleDownload() {
    const canvas = wrapRef.current?.querySelector<HTMLCanvasElement>("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${product.sku}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  return (
    <Modal
      open
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <QrCode size={16} color="#2563eb" />
          </span>
          Product QR Code
        </span>
      }
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button key="download" type="primary" icon={<Download size={14} />} onClick={handleDownload}>
          Download
        </Button>,
      ]}
      width={360}
    >
      <div ref={wrapRef} style={{ textAlign: "center", padding: "12px 0" }}>
        <QRCode value={product.sku} size={220} style={{ margin: "0 auto" }} />
        <div style={{ marginTop: 16, fontWeight: 600 }}>{product.name}</div>
        <Text type="secondary">SKU: {product.sku}</Text>
      </div>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState<string>("All");
  const [addOpen, setAddOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);
  const [qrProduct, setQrProduct] = useState<Product | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  async function fetchProducts() {
    setLoading(true);
    try {
      const resp = await axiosClient.get("/products");
      setProducts((resp.data ?? []).map(fromAPI));
    } catch {
      message.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }

  const categories = ["All", ...CATEGORIES];

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      p.name.toLowerCase().includes(q) ||
      p.brand.toLowerCase().includes(q) ||
      p.sku.toLowerCase().includes(q);
    const matchCat = selectedCat === "All" || p.category === selectedCat;
    return matchSearch && matchCat;
  });

  const totalValue = products.reduce((s, p) => s + p.price * p.stock, 0);
  const lowStockCount = products.filter((p) => p.stock <= p.lowStockThreshold).length;
  const existingSkus = new Set(products.map((p) => p.sku));

  async function handleAdd(data: Omit<Product, "id">) {
    try {
      const resp = await axiosClient.post("/products", toAPI(data));
      setProducts((prev) => [...prev, { id: resp.data.product_id, ...data }]);
      setAddOpen(false);
      message.success("Product added");
    } catch (e: unknown) {
      const err = e as { response?: { status: number } };
      if (err.response?.status === 409) {
        message.error("SKU already exists");
      } else {
        message.error("Failed to add product");
      }
    }
  }

  async function handleEdit(data: Omit<Product, "id">) {
    try {
      await axiosClient.put(`/products/${editProduct!.id}`, toAPI(data));
      setProducts((prev) =>
        prev.map((p) => (p.id === editProduct!.id ? { id: p.id, ...data } : p))
      );
      setEditProduct(null);
      message.success("Product updated");
    } catch {
      message.error("Failed to update product");
    }
  }

  async function handleDelete() {
    try {
      await axiosClient.delete(`/products/${deleteProduct!.id}`);
      setProducts((prev) => prev.filter((p) => p.id !== deleteProduct!.id));
      setDeleteProduct(null);
      message.success("Product deleted");
    } catch {
      message.error("Failed to delete product");
    }
  }

  async function handleImport(imported: Omit<Product, "id">[]) {
    try {
      const resp = await axiosClient.post("/products/bulk", imported.map(toAPI));
      await fetchProducts();
      message.success(`${resp.data.inserted} products imported`);
    } catch {
      message.error("Failed to import products");
    }
  }

  return (
    <div className="admin-products-page">
      {/* Controls bar */}
      <div className="products-controls-bar">
        <div className="products-controls-top">
          <h1 className="products-title">Products</h1>
          <Space>
            <Button icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>
              Import CSV
            </Button>
            <Button type="primary" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>
              Add Product
            </Button>
          </Space>
        </div>

        {/* Stats */}
        <div className="products-stats">
          <div className="products-stat-card products-stat-blue">
            <div className="stat-label">Total Products</div>
            <div className="stat-value">{products.length}</div>
          </div>
          <div className="products-stat-card products-stat-green">
            <div className="stat-label">Inventory Value</div>
            <div className="stat-value">${totalValue.toLocaleString()}</div>
          </div>
          <div className="products-stat-card products-stat-orange">
            <div className="stat-label">Low Stock Items</div>
            <div className="stat-value">{lowStockCount}</div>
          </div>
          <div className="products-stat-card products-stat-purple">
            <div className="stat-label">Categories</div>
            <div className="stat-value">{CATEGORIES.length}</div>
          </div>
        </div>

        {/* Search + category filters */}
        <div className="products-filters">
          <div className="products-search-wrap">
            <Search size={16} className="products-search-icon" />
            <input
              type="text"
              className="products-search-input"
              placeholder="Search by name, brand, or SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="products-search-clear" onClick={() => setSearch("")}>
                <X size={14} />
              </button>
            )}
          </div>
          <div className="products-cat-tabs">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`products-cat-tab${selectedCat === cat ? " active" : ""}`}
                onClick={() => setSelectedCat(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="products-table-wrap">
        {loading ? (
          <div className="products-empty">
            <div className="import-spinner" style={{ margin: "0 auto 12px" }} />
            <div className="products-empty-msg">Loading products…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="products-empty">
            <Package size={40} color="#e5e7eb" />
            <div className="products-empty-msg">{products.length === 0 ? "No products yet" : "No products found"}</div>
            <div className="products-empty-sub">
              {products.length === 0
                ? "Add your first product or import a CSV"
                : "Try adjusting your search or filters"}
            </div>
          </div>
        ) : (
          <>
            <table className="products-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Brand</th>
                  <th>SKU</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Price</th>
                  <th className="text-right">Margin</th>
                  <th className="text-right">Stock</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const isLow = p.stock <= p.lowStockThreshold;
                  return (
                    <tr key={p.id} className={isLow ? "row-low-stock" : ""}>
                      <td className="td-name">{p.name}</td>
                      <td>
                        <Tag color={CATEGORY_COLORS[p.category]}>{p.category}</Tag>
                      </td>
                      <td>{p.brand}</td>
                      <td className="td-sku">{p.sku}</td>
                      <td className="text-right">${p.cost.toFixed(2)}</td>
                      <td className="text-right td-price">${p.price.toFixed(2)}</td>
                      <td className="text-right td-margin">{calcMargin(p.cost, p.price)}</td>
                      <td className="text-right">
                        <span className={isLow ? "stock-low" : "stock-ok"}>{p.stock}</span>
                        {isLow && <div className="low-stock-label">Low Stock</div>}
                      </td>
                      <td className="td-actions">
                        <button className="tbl-btn" onClick={() => setEditProduct(p)} title="Edit">
                          <Edit2 size={15} />
                        </button>
                        <button className="tbl-btn" onClick={() => setQrProduct(p)} title="QR Code">
                          <QrCode size={15} />
                        </button>
                        <button className="tbl-btn tbl-btn-danger" onClick={() => setDeleteProduct(p)} title="Delete">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="products-table-footer">
              <Text type="secondary">
                Showing {filtered.length} of {products.length} products
              </Text>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {addOpen && <ProductModal onClose={() => setAddOpen(false)} onSave={handleAdd} />}
      {editProduct && <ProductModal product={editProduct} onClose={() => setEditProduct(null)} onSave={handleEdit} />}
      {deleteProduct && <DeleteModal product={deleteProduct} onClose={() => setDeleteProduct(null)} onConfirm={handleDelete} />}
      {qrProduct && <QRModal product={qrProduct} onClose={() => setQrProduct(null)} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onImport={handleImport} existingSkus={existingSkus} />}
    </div>
  );
}
